import { useState } from 'react';
import { testimonials } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9u3ms53pcjqs2fcsld0';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formDataObj = new FormData(form);

    const honeypot = (formDataObj.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    formDataObj.delete('phone_alt');

    const params = new URLSearchParams();
    formDataObj.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      const res = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - caoliushequ官方导航"
        description="了解caoliushequ品牌故事、用户口碑评价及联系方式。caoliushequ是深受用户喜爱的内容社区平台，致力于为用户提供优质的社区服务。"
        keywords="caoliushequ关于,caoliushequ品牌,caoliushequ联系方式,caoliushequ口碑"
        canonical="https://nammage.cn/about"
        ogTitle="关于我们 - caoliushequ官方导航"
        ogDescription="了解caoliushequ品牌故事、用户口碑评价及联系方式。"
        ogUrl="https://nammage.cn/about"
      />

      <SchemaOrg type="Organization" />

      <div className="min-h-screen pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10">
          <header className="mb-12 text-center">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 mb-4">
              关于 caoliushequ
            </h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              caoliushequ是深受用户喜爱的内容社区平台，以丰富多元的内容和友善包容的社区氛围赢得了广大用户的信赖。
            </p>
          </header>

          <section className="mb-14">
            <h2 className="text-xl font-heading text-foreground-900 mb-5">品牌介绍</h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    caoliushequ致力于打造一个开放、包容、高质量的内容社区平台。我们相信每个人都有值得分享的观点和经验，每个声音都值得被倾听。
                  </p>
                  <h3 className="text-base font-semibold text-foreground-800 mb-3">核心价值观</h3>
                  <ul className="text-sm text-foreground-600 leading-relaxed space-y-2">
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" />
                      <span><strong>用户至上</strong> — 始终以用户体验为核心，持续优化产品</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" />
                      <span><strong>内容为王</strong> — 鼓励原创优质内容，构建健康内容生态</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0" />
                      <span><strong>社区共建</strong> — 与用户共同成长，携手建设优质社区</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Warm%20modern%20community%20office%20space%20with%20diverse%20team%20collaborating%2C%20natural%20light%20streaming%20through%20windows%2C%20creative%20atmosphere%2C%20clean%20minimalist%20design%2C%20professional%20editorial%20photography&width=600&height=400&seq=about-brand-01&orientation=landscape"
                    alt="caoliushequ品牌"
                    title="caoliushequ品牌 - 社区办公环境"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl font-heading text-foreground-900 mb-5">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-xs font-medium text-secondary-700">
                        {t.author.charAt(0)}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-foreground-800">{t.author}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14">
            <h2 className="text-xl font-heading text-foreground-900 mb-5">联系我们</h2>
            <div className="bg-background-100 rounded-xl p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-4">联系方式</h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                        <i className="ri-mail-line" />
                      </div>
                      <span>support@nammage.cn</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                        <i className="ri-global-line" />
                      </div>
                      <span>https://nammage.cn</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground-800 mb-4">在线留言</h3>
                  <form onSubmit={handleSubmit} className="space-y-3" data-readdy-form="">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的称呼"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      required
                    />
                    <textarea
                      name="message"
                      placeholder="您的留言（最多500字）"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      required
                    />
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="hp-field"
                    />
                    <button
                      type="submit"
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">留言已发送，感谢您的反馈！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </article>
      </div>
    </>
  );
}